import { css } from '@emotion/react';
import { DashboardWidget, DashboardWidgetFC } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, styles } from '@frontend/design-system';
import { ScheduleRequestWidgetList } from '../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests';
import { useGetPendingRequestsV3InfiniteQuery } from '../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks';
import { useLocationFilterMenu } from './hooks/use-location-filter-menu';

/**
 * @dashboard-widget
 *
 * id: schedule-requests-widget
 * title: Appointment Requests
 * description: Effortlessly manage patient requests with a list view of all requested appointments.
 * icon: calendar-small
 */

export const ScheduleRequestsWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('schedule');
  const { selectedLocationIds } = useAppScopeStore();
  const locationFilterMenuProps = useLocationFilterMenu(selectedLocationIds);
  const { selectedOptions: filteredLocationIds } = locationFilterMenuProps;

  const infiniteListQuery = useGetPendingRequestsV3InfiniteQuery(filteredLocationIds);
  const totalRequests = infiniteListQuery.data?.pages.flatMap((page) => page.rows).length ?? 0;

  return (
    <DashboardWidget>
      <DashboardWidget.Header>
        <LocationFilterMenu {...locationFilterMenuProps} showClearAll={false} />
      </DashboardWidget.Header>
      <DashboardWidget.Content css={containerStyle}>
        <>
          <div
            css={[
              styles.flexCenter,
              { display: 'flex', flexDirection: 'column', gap: theme.spacing(1), marginBottom: theme.spacing(1) },
            ]}
          >
            <Text weight='bold' css={{ fontSize: theme.fontSize(104), lineHeight: 1 }}>
              {totalRequests}
            </Text>
            <Text color='light'>{t('Pending Requests')}</Text>
          </div>
          <div css={listContainerStyle}>
            {!!totalRequests ? (
              <ScheduleRequestWidgetList
                infiniteListQuery={infiniteListQuery}
                refetchScheduleRequestList={infiniteListQuery.refetch}
                height={'100%'}
              />
            ) : (
              <div css={[styles.flexCenter, { height: '100%', justifyContent: 'center' }]}>
                <Text color='light' size='medium'>
                  {t('No Appointment Requests found')}
                </Text>
              </div>
            )}
          </div>
        </>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

ScheduleRequestsWidget.config = {
  size: {
    extraSmall: 'large-narrow',
    small: 'large-narrow',
    medium: 'large-wide',
    large: 'large-wide',
  },
  feature: 'schedule',
};

const containerStyle = css({
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 1,
  overflow: 'hidden',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
  alignItems: 'flex-start',
});

const listContainerStyle = css({
  flexGrow: 1,
  background: theme.colors.neutral5,
  padding: theme.spacing(1),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
});
